import { useEffect, useRef, useCallback } from 'react';

/**
 * 주기적으로 데이터를 가져오는 커스텀 훅
 * @param {Function} fetchFunction - 실행할 비동기 함수
 * @param {number} interval - 실행 간격 (밀리초, 기본값: 10000ms = 10초)
 * @param {Array} dependencies - 의존성 배열 (이 값들이 변경되면 타이머가 재시작됨)
 * @param {boolean} enabled - 주기적 실행 활성화 여부 (기본값: true)
 */
const usePeriodicFetch = (fetchFunction, interval = 10000, dependencies = [], enabled = true) => {
  const intervalRef = useRef(null);
  const isFetchingRef = useRef(false);

  // 안전한 fetch 함수 (중복 실행 방지)
  const safeFetch = useCallback(async () => {
    if (isFetchingRef.current) {
      console.log('이전 fetch가 아직 진행 중입니다. 건너뜁니다.');
      return;
    }

    try {
      isFetchingRef.current = true;
      await fetchFunction();
    } catch (error) {
      console.error('주기적 데이터 가져오기 실패:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchFunction]);

  // 주기적 실행 시작
  const startPeriodicFetch = useCallback(() => {
    // 기존 타이머가 있으면 정리
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 즉시 한 번 실행
    safeFetch();

    // 주기적 실행 설정
    intervalRef.current = setInterval(() => {
      safeFetch();
    }, interval);

    console.log(`주기적 데이터 가져오기 시작 (${interval}ms 간격)`);
  }, [safeFetch, interval]);

  // 주기적 실행 중지
  const stopPeriodicFetch = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('주기적 데이터 가져오기 중지');
    }
  }, []);

  // 페이지 가시성 변경 처리 (백그라운드에서는 중지)
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // 페이지가 숨겨졌을 때는 주기적 실행 중지
      stopPeriodicFetch();
    } else {
      // 페이지가 다시 보일 때 주기적 실행 시작
      if (enabled) {
        startPeriodicFetch();
      }
    }
  }, [enabled, startPeriodicFetch, stopPeriodicFetch]);

  // 컴포넌트 마운트/언마운트 및 의존성 변경 시 처리
  useEffect(() => {
    if (enabled) {
      // 주기적 실행 시작
      startPeriodicFetch();

      // 페이지 가시성 이벤트 리스너 등록
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        // 정리 함수
        stopPeriodicFetch();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // enabled가 false이면 중지
      stopPeriodicFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, interval, ...dependencies]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopPeriodicFetch();
    };
  }, [stopPeriodicFetch]);

  return {
    startPeriodicFetch,
    stopPeriodicFetch,
    isActive: intervalRef.current !== null
  };
};

export default usePeriodicFetch;

